import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { FaEnvelope, FaHeart, FaRegHeart, FaComment } from 'react-icons/fa';
import { MdOutlineMenuBook } from 'react-icons/md';
import api from '../services/api';
import '../styles/Community.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';  // Import the utility function
import getBookCoverUrl from '../utils/getBookCoverUrl'
function Community() {
  const { profileUserId } = useParams();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const [user, setUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [createPost, setCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [communityPosts, setCommunityPosts] = useState([]);
  useEffect(() => {
    if (!profileUserId) return;
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchUserProfile = async () => {
      try {
     
        const response = await api.get(`/profile/${profileUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
     
        const { user: userData, books: userBooks, isFollowing: followStatus } = response.data;
        const profilePhoto = getProfileImageUrl(userData.profile_image_url); 
        setUser({
          ...userData,
          handle: `@${userData.username}`,
          joinDate: new Date(userData.join_date).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          profilePhoto,
          readingList: userBooks.length,
        });
        setIsFollowing(followStatus);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    const fetchCurrentUser = async () => {
      try {
       
        const response = await api.get(`/profile/${currentUserId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
   
        const currentUserData = response.data.user;
        const currentUserProfilePhoto = getProfileImageUrl(currentUserData.profile_image_url);
        setCurrentUser({
          username: currentUserData.username,
          profilePhoto: currentUserProfilePhoto,
        });
      } catch (error) {
        console.error("Error fetching current user's details:", error);
      }
    };
    const fetchCommunityPosts = async () => {
      try {
        
        const response = await api.get(`/community/user/${profileUserId}/posts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
   
        const postsWithComments = await Promise.all(
          response.data.map(async (post) => {
            const commentsResponse = await api.get(
              `/community/posts/${post.post_id}/comments`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            const commentsWithLikes = commentsResponse.data.map((comment) => ({
              ...comment,
              isLiked: comment.is_liked,
              profile_image_url: getProfileImageUrl(comment.profile_image_url),
            }));
            const profileImageURL = getProfileImageUrl(post.profile_image_url);
            return {
              ...post,
              profile_image_url: profileImageURL,
              comments: commentsWithLikes,
              newComment: '',
              showComments: false,
              isLiked: post.is_liked,
            };
          })
        );
        setCommunityPosts(postsWithComments);
      } catch (error) {
        console.error('Error fetching community posts:', error);
      }
    };
    fetchUserProfile();
    fetchCurrentUser();
    fetchCommunityPosts();
  }, [navigate, profileUserId, currentUserId]);
  const handleFollowClick = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      if (isFollowing) {
        await api.post(`/profile/unfollow/${profileUserId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post(`/profile/follow/${profileUserId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setIsFollowing(!isFollowing);
      setUser((prevUser) => ({
        ...prevUser,
        followers_count: isFollowing ? prevUser.followers_count - 1 : prevUser.followers_count + 1,
      }));
    } catch (error) {
      console.error('Error updating follow status:', error);
    }
  };
  const handleMessageClick = () => {
    navigate('/inbox');
  };
  const handleProfileClick = () => {
    navigate(`/profile/${profileUserId}`);
  };
  const handleLike = async (postId, commentId = null) => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId || isNaN(postId)) {
      console.error('Invalid token, userId, or postId');
      navigate('/login');
      return;
    }
    try {
    
      const url =
        commentId === null
          ? `/community/posts/${postId}/like`
          : `/community/comments/${commentId}/like`;
      const payload = { userId: parseInt(userId) };
      await api.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommunityPosts((prevPosts) =>
        prevPosts.map((post) => {
          if (commentId === null && post.post_id === postId) {
            return {
              ...post,
              likes_count: post.isLiked ? post.likes_count - 1 : post.likes_count + 1,
              isLiked: !post.isLiked,
            };
          } else if (post.post_id === postId) {
            return {
              ...post,
              comments: post.comments.map((comment) =>
                comment.comment_id === commentId
                  ? {
                    ...comment,
                    likes_count: comment.isLiked ? comment.likes_count - 1 : comment.likes_count + 1,
                    isLiked: !comment.isLiked,
                  }
                  : comment
              ),
            };
          }
          return post;
        })
      );
    } catch (error) {
      console.error('Error toggling like on post or comment:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    }
  };
  const toggleComments = (postId) => {
    setCommunityPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.post_id === postId ? { ...post, showComments: !post.showComments } : post
      )
    );
  };
  const handleCommentChange = (postId, value) => {
    setCommunityPosts((prevPosts) =>
      prevPosts.map((post) =>
        post.post_id === postId ? { ...post, newComment: value } : post
      )
    );
  };
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId) {
      console.error('Invalid token or userId');
      navigate('/login');
      return;
    }
    if (newPostContent.trim()) {
      try {
        const response = await api.post(
          `/community/posts`,
          {
            userId: parseInt(userId),
            content: newPostContent,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const newPost = {
          post_id: response.data.postId,
          user_id: parseInt(userId),
          profile_image_url: getProfileImageUrl(user.profilePhoto),
          username: user.username,
          content: newPostContent,
          created_at: new Date().toISOString(),
          likes_count: 0,
          isLiked: false,
          comments: [],
          showComments: false,
          newComment: '',
        };
        setCommunityPosts([newPost, ...communityPosts]);
        setNewPostContent('');
        setCreatePost(false);
      } catch (error) {
        console.error('Error adding community post:', error);
        console.error('Error details:', error.response ? error.response.data : error.message);
      }
    }
  };
  const handleCommentSubmit = async (postId, e) => {
    e.preventDefault();
    const post = communityPosts.find((p) => p.post_id === postId);
    if (!post.newComment.trim() || !currentUser) return;
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId || isNaN(postId)) {
      console.error('Invalid token, userId, or postId');
      navigate('/login');
      return;
    }
    try {
      const response = await api.post(
        `/community/posts/${postId}/comments`,
        {
          userId: parseInt(userId),
          postId,
          content: post.newComment,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const newComment = {
        comment_id: response.data.commentId,
        user_id: parseInt(userId),
        profile_image_url: getProfileImageUrl(currentUser.profilePhoto), 
        username: currentUser.username, 
        content: post.newComment,
        created_at: new Date().toISOString(),
        likes_count: 0,
        isLiked: false,
      };
      setCommunityPosts((prevPosts) =>
        prevPosts.map((p) =>
          p.post_id === postId ? { ...p, comments: [...p.comments, newComment], newComment: '' } : p
        )
      );
    } catch (error) {
      console.error('Error adding post comment:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    }
  };
  const handleDeletePost = async (postId) => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId || isNaN(postId)) {
      console.error('Invalid token, userId, or postId');
      navigate('/login');
      return;
    }
    try {
      await api.delete(`/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommunityPosts((prevPosts) => prevPosts.filter((post) => post.post_id !== postId));
    } catch (error) {
      console.error('Error deleting community post:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    }
  };
  const handleDeleteComment = async (postId, commentId) => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    if (!token || !userId || isNaN(commentId)) {
      console.error('Invalid token, userId, or commentId');
      navigate('/login');
      return;
    }
    try {
      await api.delete(`/community/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCommunityPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? {
              ...post,
              comments: post.comments.filter((comment) => comment.comment_id !== commentId)
            }
            : post
        )
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
      console.error('Error details:', error.response ? error.response.data : error.message);
    }
  };
if (!user || !currentUser) {
    return (
      <div className="community-loading-container">
        <div className="community-loading-spinner"></div>
      </div>
    );
  }

  const isOwnProfile = profileUserId === currentUserId;

  return (
    <div className="community-profile-container">
      {isOwnProfile && createPost && (
        <div className="community-modal-overlay" onClick={() => setCreatePost(false)}>
          <div className="community-create-post-modal" onClick={(e) => e.stopPropagation()}>
            <button className="community-modal-close-button" onClick={() => setCreatePost(false)}>&times;</button>
            <form onSubmit={handlePostSubmit}>
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="Paylaşımınızı yazın..."
              />
              <button type="submit">Paylaş</button>
            </form>
          </div>
        </div>
      )}

      <div className="community-profile-cover" style={{ backgroundImage: `url(${getBookCoverUrl(user.coverPhoto || '')})` }}></div>

      <div className="community-profile-info-section">
        <div className="community-profile-header">
          <div className="community-avatar-wrapper">
            {user.profilePhoto ? (
              <img src={user.profilePhoto} alt={user.username} className="community-profile-avatar" />
            ) : (
              <div className="community-profile-avatar-placeholder">
                {user.username[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="community-profile-meta">
            <h1>{user.full_name}</h1>
            <p className="community-user-handle">{user.handle}</p>
          </div>
          <div className="community-follow-button-container">
            {!isOwnProfile && (
              <button className="community-follow-button" onClick={handleFollowClick}>
                {isFollowing ? 'Takip Ediliyor' : 'Takip Et'}
              </button>
            )}
            <FaEnvelope className="community-icon" onClick={handleMessageClick} />
            <MdOutlineMenuBook className="community-icon" onClick={handleProfileClick} />
          </div>
        </div>
      </div>

      <div className="community-profile-stats">
        <span>{user.readingList} <span className="community-stat-label">Kitaplar</span></span>
        <span>{user.followers_count} <span className="community-stat-label">Takipçiler</span></span>
        <span>{user.following_count} <span className="community-stat-label">Takip</span></span>
      </div>

      <div className="community-profile-about">
        <h3>Hakkında</h3>
        <p>{user.bio}</p>
        <div className="community-join-date">
          <Calendar className="community-icon" /> Üye Oldu: {user.joinDate}
        </div>
      </div>

      <div className="community-posts-section">
        <h2>{user.full_name} tarafından yapılmış paylaşımlar</h2>
        {isOwnProfile && (
          <button className="community-create-post-button" onClick={() => setCreatePost(true)}>
            Yeni Paylaşım
          </button>
        )}

        {communityPosts && communityPosts.length > 0 ? (
          <ul className="community-posts-list">
            {communityPosts.map((post) => (
              <li key={post.post_id} className="community-post-item">
                <div className="community-post-header">
                  <img
                    src={getProfileImageUrl(post.profile_image_url)}
                    alt={post.username}
                    className="community-avatar"
                  />
                  <div className="community-post-user-info">
                    <div className="community-user-info-header">
                      <Link to={`/profile/${post.user_id}`} className="community-author-name">
                        {post.username}
                      </Link>
                    </div>
                    <span className="community-post-date">{new Date(post.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                </div>

                <div className="community-post-content">
                  <p>{post.content}</p>
                  <div className="community-post-actions">
                    <div className="community-interaction-buttons">
                      <button className="community-action-button" onClick={() => handleLike(post.post_id)}>
                        {post.isLiked ? <FaHeart color="#ff4b4b" /> : <FaRegHeart />}
                        <span>{isNaN(post.likes_count) ? 0 : post.likes_count}</span>
                      </button>
                      <button className="community-action-button" onClick={() => toggleComments(post.post_id)}>
                        <FaComment />
                        <span>{post.comments?.length || 0}</span>
                      </button>
                      {parseInt(post.user_id) === parseInt(currentUserId) && (
                        <button className="community-delete-action-button" onClick={() => handleDeletePost(post.post_id)}>
                          Sil
                        </button>
                      )}
                    </div>
                  </div>

                  {post.showComments && post.comments && (
                    <div className="community-comments-section">
                      {post.comments.map((comment) => (
                        <div key={comment.comment_id} className="community-comment">
                          <div className="community-comment-header">
                            <img
                              src={getProfileImageUrl(comment.profile_image_url)}
                              alt={comment.username}
                              className="community-comment-avatar"
                            />
                            <div className="community-comment-info">
                              <div className="community-user-info-header">
                                <Link to={`/profile/${comment.user_id}`} className="community-comment-author">
                                  {comment.username}
                                </Link>
                              </div>
                              <span className="community-comment-date">{new Date(comment.created_at).toLocaleString('tr-TR')}</span>
                            </div>
                          </div>
                          <div className="community-comment-body">
                            <span className="community-comment-content">{comment.content}</span>
                          </div>
                          <div className="community-comment-footer">
                            <button
                              className="community-action-button"
                              onClick={() => handleLike(post.post_id, comment.comment_id)}
                            >
                              {comment.isLiked ? <FaHeart color="#ff4b4b" /> : <FaRegHeart />}
                              <span>{isNaN(comment.likes_count) ? 0 : comment.likes_count}</span>
                            </button>
                            {parseInt(comment.user_id) === parseInt(currentUserId) && (
                              <button className="community-delete-action-button" onClick={() => handleDeleteComment(post.post_id, comment.comment_id)}>
                                Sil
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <form className="community-comment-form" onSubmit={(e) => handleCommentSubmit(post.post_id, e)}>
                        <input
                          type="text"
                          placeholder="Yorumunuzu yazın..."
                          value={post.newComment}
                          onChange={(e) => handleCommentChange(post.post_id, e.target.value)}
                        />
                        <button type="submit">Gönder</button>
                      </form>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>Henüz paylaşım yapılmamış.</p>
        )}
      </div>
    </div>
  );
}

export default Community;
